import { useCallback } from "react";

import {
  ComplianceService,
  type MultiFrameworkReadinessResponse,
  type ReadinessResponse,
} from "@/api";
import { useApi } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import {
  formatFrameworkCode,
  formatReadiness,
  readinessTone,
} from "@/lib/compliance-helpers";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

export function CompliancePostureTab() {
  const { t } = useTranslation();
  const fetcher = useCallback(() => ComplianceService.getReadiness(), []);
  const { data, error, isLoading } =
    useApi<MultiFrameworkReadinessResponse>(fetcher);

  if (error) {
    return <Alert variant="warning">{parseApiError(error)}</Alert>;
  }

  if (isLoading && !data) {
    return (
      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 desktop:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  const items = data?.items ?? [];

  if (items.length === 0) {
    return <Alert variant="info">{t("compliance.posture.empty")}</Alert>;
  }

  return (
    <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 desktop:grid-cols-3">
      {items.map((item) => (
        <PostureCard key={item.framework_code} item={item} />
      ))}
    </div>
  );
}

function PostureCard({ item }: { item: ReadinessResponse }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-ocean-100 bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="label-eyebrow">
            {t("compliance.posture.cardFramework")}
          </div>
          <div className="mt-1 text-base font-medium text-ocean-900">
            {formatFrameworkCode(item.framework_code)}
          </div>
        </div>
        <Badge tone={readinessTone(item.readiness_score)}>
          {formatReadiness(item.readiness_score)}
        </Badge>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="label-eyebrow">
            {t("compliance.posture.coveredClauses")}
          </div>
          <div className="mt-1 text-ocean-900">
            {item.covered_clauses} / {item.total_clauses}
          </div>
        </div>
        <div>
          <div className="label-eyebrow">
            {t("compliance.posture.criticalClauses")}
          </div>
          <div className="mt-1 text-ocean-900">
            {item.critical_clauses_covered} / {item.critical_clauses_total}
            {item.critical_clauses_total > 0 && (
              <span className="ml-1 text-xs text-neutral-500">
                ({item.critical_coverage_pct.toFixed(0)}%)
              </span>
            )}
          </div>
        </div>
      </div>

      {item.gap_clauses.length > 0 && (
        <p className="mt-3 text-xs text-warning-600">
          {t("compliance.posture.gapNotice", {
            count: item.gap_clauses.length,
          })}
        </p>
      )}
    </div>
  );
}
